import { useEffect, useMemo, useState } from 'react';
import { ServiceNodeGroup } from '@bumble/app/__generated__/data-contracts';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { ServiceNodeGroupEnableCert } from '@bumble/app/components/service-node-group';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';

const columns: GridColDef[] = [
  {
    field: 'service_node_group_name',
    headerName: 'Node Group Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'service_node_group_id',
    headerName: 'Node Group Id',
    width: 200,
    align: 'left',
    disableColumnMenu: true,
  },
  {
    field: 'external_certificate',
    headerName: 'External Certificate',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return params.value ? 'Yes' : 'No';
    },
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  service_node_group_name: true,
  service_node_group_id: true,
  external_certificate: true,
  created_at: true,
};

export const ServiceNodeGroups = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedGroup, setSelectedGroup] = useState<ServiceNodeGroup | null>(null);

  const api = useApi();

  const { isLoading, data, refetch } = useQuery(
    ['serviceNodeGroups'],
    api.listServiceNodeGroupApiV1ListServiceNodeGroupGet.bind(null, {}),
    { 
      enabled: true,
      refetchOnMount: true, // To ensure service node group info is current
    },
  );

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Service Node Groups.');
  };

  const [showEnableCert, setShowEnableCert] = useState<boolean>(false);
  const [showDisableCert, setShowDisableCert] = useState<boolean>(false);

  const handleEnableCert = () => {
    setShowEnableCert(true);
  };

  const selectedRowGroup = useMemo(() => {
    if (rowSelectionModel.length !== 1) return null;
    const itemId = rowSelectionModel[0].toString();
    return data?.data.find((item) => item.service_node_group_id === itemId) || null;
  }, [data, rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedGroup(selectedRowGroup);
    } else {
      setSelectedGroup(null);
    }
  }, [rowSelectionModel, selectedRowGroup]);

  const disableExternalCert = useMutation(
    api.disableExternalCertificateApiV1DisableExternalCertificatePost,
    {
      onSuccess() {
        toast.success('External Root CA disabled');
        handleDataRefetch();
      },
      onError: apiError,
    },
  );

  const handleDisableCert = () => {
    disableExternalCert.mutate({
      service_node_group_id: selectedGroup?.service_node_group_id,
    });
  };
  return (
    <PageContent>
      <PageHeader
        pageName="Manage Service Node Groups"
        breadcrumbs={[{ label: 'Service Nodes', to: '/service-nodes' }]}
        count={data?.data?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                data?.data.map((item) => {
                  return {
                    ...item,
                    id: item.service_node_group_id,
                  };
                }) || []
              }
              columns={columns}
              handleSelectionChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              itemsSelected={rowSelectionModel.length === 1}
              columnVisibilityModel={columnVisible}
              refreshData={handleDataRefetch}
              menuItems={[
                {
                  label: 'Import External Certificates',
                  icon: <CheckCircleOutlineOutlinedIcon color="success" fontSize="small" />,
                  onClick: handleEnableCert,
                  disabled: selectedGroup?.external_certificate,
                },
                {
                  label: 'Disable External Root CA',
                  icon: <NotInterestedOutlinedIcon color="info" fontSize="small" />,
                  onClick: () => setShowDisableCert(true),
                  disabled: !selectedGroup?.external_certificate,
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {showEnableCert && selectedGroup && (
        <DialogStandard
          open={showEnableCert}
          handleClose={() => setShowEnableCert(false)}
          title="Import External Certificates"
        >
          <ServiceNodeGroupEnableCert
            group={selectedGroup}
            onSubmit={() => {
              setShowEnableCert(false);
              handleDataRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showDisableCert && selectedGroup && (
        <DialogConfirm
          open={showDisableCert}
          handleClose={() => setShowDisableCert(false)}
          handleConfirm={handleDisableCert}
          title="Disable External Root CA"
          description="Disabling external Root Certificate Authorities (CAs) will restrict the service node group to use only internal or pre-approved certificates for encrypted communications. Do you want to proceed?"
          confirmationPhrase="confirm"
        />
      )}
    </PageContent>
  );
};
